/**
 * Light, dark, or whatever the machine says.
 *
 * Three states rather than two, because "follow my system" is a real preference and
 * collapsing it into a boolean means a reader who switches their laptop to dark at
 * sunset has to come here and switch this too.
 *
 * The chosen value is written to the root element as `data-theme`, which is what
 * the stylesheet keys off. Absence of the attribute IS the system state — there is
 * no `data-theme="system"`, because then the CSS would need to special-case it.
 */

export type Theme = "light" | "dark" | "system";

const KEY = "zain-credits-theme";

export function readTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    // Private browsing, or storage disabled by policy. Following the system is the
    // right answer when we cannot remember a choice — not silently forcing light.
    return "system";
  }
}

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
  try {
    if (theme === "system") window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, theme);
  } catch {
    // Not being able to remember the choice must not stop it applying now.
  }
}

/**
 * Run before React mounts. Reading the stored value only inside a component means
 * the first paint is light and then flips, which is the flash every themed site is
 * judged by.
 */
export function initTheme() {
  applyTheme(readTheme());
}
